/**
 * VerificationPane — FTUX onboarding's observable "why" view.
 *
 * Renders only the editable profile form. Citations live on the dialogue
 * messages (Sources tab); answer-details and the greeting-module profile
 * citation card were intentionally removed because they cluttered the pane.
 *
 * Fires `verification_opened` telemetry on mount (mount = user switched to
 * this tab, which is our definition of "opened the Verification Pane").
 */

import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  CheckCircleOutline as CheckIcon,
  EditNoteOutlined,
} from "@mui/icons-material";
import { green } from "@mui/material/colors";
import axios from "axios";

import { nuclearn_api_url } from "src/App";
import { useOnboardingStore } from "../../stores/OnboardingStore";
import { reportOnboardingEvent } from "../../services/OnboardingService";
import { useUpdateUserProfile } from "src/views/users/ProfileService";
import { NucToast } from "src/reusable/components/Toast";

function ProfileFormEditCard({ dialogueId }: { dialogueId: number | null }) {
  const {
    profileFormFields,
    profileFormValues,
    profileFormSubmitted,
    setProfileFormValue,
    setProfileFormSubmitted,
  } = useOnboardingStore();

  const { mutateAsync: updateUserProfile } = useUpdateUserProfile();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [justSavedAt, setJustSavedAt] = useState<number | null>(null);

  const fields = profileFormFields ?? [];

  // First save unblocks the FTUX input queue; once that's happened the
  // queue is gone, so subsequent edits go straight to the profile API.
  const handleSave = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const values: Record<string, string> = {};
      for (const f of fields) {
        values[f.field] = (profileFormValues[f.field] ?? "").trim();
      }

      if (!profileFormSubmitted) {
        if (!dialogueId) return;
        const payload = JSON.stringify({
          user_form_submission: {
            values,
            submitted_at: new Date().toISOString(),
          },
        });
        await axios.post(`${nuclearn_api_url}/chat/send/${dialogueId}`, {
          content: payload,
        });
        setProfileFormSubmitted(true);
      } else {
        await updateUserProfile(values);
      }
      setJustSavedAt(Date.now());
    } catch {
      // Don't swallow silently — without a toast the user sees the Save
      // button re-enable and has no indication anything failed. Form
      // stays editable (profileFormSubmitted + values unchanged) so retry
      // is immediate.
      NucToast({
        message:
          "Couldn't save your profile — check your connection and try again.",
        type: "error",
        hideProgressBar: false,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Hide the "Saved just now" flash after a couple seconds so it doesn't
  // linger and confuse a user who makes another edit.
  useEffect(() => {
    if (justSavedAt === null) return;
    const handle = window.setTimeout(() => setJustSavedAt(null), 2500);
    return () => window.clearTimeout(handle);
  }, [justSavedAt]);

  if (fields.length === 0) return null;

  const showSavedFlash = justSavedAt !== null;

  return (
    <Paper
      data-testid="aa-ftux-verify-form"
      variant="outlined"
      sx={{
        p: 2,
        borderColor: profileFormSubmitted ? green[300] : undefined,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        <EditNoteOutlined fontSize="small" color="primary" />
        <Typography
          data-testid="aa-ftux-verify-prompt"
          variant="subtitle2"
          fontWeight={600}
        >
          Your Profile
        </Typography>
        {(profileFormSubmitted || showSavedFlash) && (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            icon={<CheckIcon />}
            label={showSavedFlash ? "Saved" : "Saved earlier"}
            sx={{ ml: "auto" }}
          />
        )}
      </Stack>
      <Stack spacing={2}>
        {fields.map((f) => (
          <TextField
            key={f.field}
            label={f.label}
            placeholder={f.prompt}
            size="small"
            fullWidth
            disabled={isSubmitting}
            value={profileFormValues[f.field] ?? ""}
            onChange={(e) => setProfileFormValue(f.field, e.target.value)}
          />
        ))}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isSubmitting || (!profileFormSubmitted && !dialogueId)}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
          sx={{ alignSelf: "flex-start" }}
        >
          {profileFormSubmitted ? "Save changes" : "Save and continue"}
        </Button>
      </Stack>
    </Paper>
  );
}

function EmptyState() {
  return (
    <Box
      data-testid="aa-ftux-verify-empty-state"
      sx={{ p: 3, textAlign: "center" }}
    >
      <Typography variant="body2" color="text.secondary">
        Your profile form will appear here once onboarding kicks off so you can
        review and edit what AtomAssist knows about you.
      </Typography>
    </Box>
  );
}

export function VerificationPane() {
  const { profileFormFields, onboardingDialogueId } = useOnboardingStore();

  // Fire verification_opened exactly once per (mount × dialogue id). Using
  // [onboardingDialogueId] as a dep would re-fire whenever it changes —
  // notably on reload when it hydrates null → number, double-counting.
  // A ref of dialogue ids we've already reported gives us per-session
  // dedup without tracking global state.
  const reportedForDialogueRef = useRef<Set<number | string | null>>(new Set());
  useEffect(() => {
    const key = onboardingDialogueId;
    if (reportedForDialogueRef.current.has(key)) return;
    reportedForDialogueRef.current.add(key);
    reportOnboardingEvent("verification_opened", {
      dialogue_id: onboardingDialogueId,
    }).catch(() => {
      // Telemetry is best-effort — never block the UI.
    });
  }, [onboardingDialogueId]);

  return (
    <Box data-testid="aa-ftux-verify-pane">
      {!profileFormFields || profileFormFields.length === 0 ? (
        <EmptyState />
      ) : (
        <Stack spacing={2} sx={{ p: 2 }}>
          <ProfileFormEditCard dialogueId={onboardingDialogueId} />
        </Stack>
      )}
    </Box>
  );
}

export default VerificationPane;
